import React, { useMemo, useState, useEffect } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import {
  TrendingUp,
  Users,
  MapPin,
  Trophy,
  BarChart3,
  Activity,
  UserPlus,
  CalendarDays,
  Award,
  Flame,
  ShieldCheck,
  Clock
} from 'lucide-react';
import { Player, Match, Tournament, UserProfile } from '../../types';

interface SuperAdminAnalyticsProps {
  players: Player[];
  matches?: Match[];
  tournaments?: Tournament[];
  sedesPublicas: UserProfile[];
  allUsers?: UserProfile[];
}

export function SuperAdminAnalytics({
  players,
  sedesPublicas,
  allUsers = []
}: SuperAdminAnalyticsProps) {
  const [matches, setMatches] = useState<Match[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);

  useEffect(() => {
    const unsubMatches = onSnapshot(collection(db, getCollectionPath('matches')), snap => {
      setMatches(snap.docs.map(d => ({ id: d.id, ...d.data() } as Match)));
    });
    const unsubTournaments = onSnapshot(collection(db, getCollectionPath('tournaments')), snap => {
      setTournaments(snap.docs.map(d => ({ id: d.id, ...d.data() } as Tournament)));
    });
    return () => {
      unsubMatches();
      unsubTournaments();
    };
  }, []);

  // ── KPIs PRINCIPALES ──
  const kpis = useMemo(() => {
    const now = Date.now();
    const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
    const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);

    const totalPlayers = players.length;
    const activeSedes = sedesPublicas.filter(s => s.role === 'admin').length;
    const totalMatches = matches.length;
    const totalTournaments = tournaments.length;

    // Nuevos jugadores este mes
    const newPlayersMonth = players.filter(p => p.createdAt >= thirtyDaysAgo).length;
    const newPlayersWeek = players.filter(p => p.createdAt >= sevenDaysAgo).length;

    // Jugadores validados vs pendientes
    const validatedPlayers = players.filter(p => p.isValidated).length;
    const pendingValidation = players.filter(p => !p.isValidated && !p.validationRejected && !p.validationReviewed).length;

    // Jugadores "on fire"
    const onFirePlayers = players.filter(p => p.isOnFire).length;

    // Torneos activos (sin fecha de finalización o en curso)
    const activeTournaments = tournaments.filter(t => t.status === 'active' || t.status === 'in_progress').length;

    const totalAppUsers = allUsers.length;

    return {
      totalPlayers,
      totalAppUsers,
      activeSedes,
      totalMatches,
      totalTournaments,
      newPlayersMonth,
      newPlayersWeek,
      validatedPlayers,
      pendingValidation,
      onFirePlayers,
      activeTournaments
    };
  }, [players, matches, tournaments, sedesPublicas, allUsers]);

  // ── DEMOGRAFÍA DE JUGADORES ──
  const demographics = useMemo(() => {
    const total = players.length || 1;
    // Género
    const male = players.filter(p => p.gender?.toLowerCase() === 'masculino' || p.gender?.toLowerCase() === 'hombre' || p.gender?.toLowerCase() === 'm').length;
    const female = players.filter(p => p.gender?.toLowerCase() === 'femenino' || p.gender?.toLowerCase() === 'mujer' || p.gender?.toLowerCase() === 'f').length;
    const otherGender = total - male - female;
    // Mano
    const diestro = players.filter(p => p.hand === 'diestro').length;
    const zurdo = players.filter(p => p.hand === 'zurdo').length;
    const handUnset = total - diestro - zurdo;
    // Posición
    const drive = players.filter(p => p.position === 'drive').length;
    const reves = players.filter(p => p.position === 'reves').length;
    const ambas = players.filter(p => p.position === 'ambas').length;
    const posUnset = total - drive - reves - ambas;
    // Logros
    const withAchievements = players.filter(p => p.achievements && p.achievements.length > 0).length;
    const totalAchievements = players.reduce((acc, p) => acc + (p.achievements?.length || 0), 0);
    // Suspendidos
    const suspended = players.filter(p => p.isSuspended).length;
    // Invitados
    const guests = players.filter(p => p.isGuest).length;
    // Con foto
    const withPhoto = players.filter(p => p.photoUrl).length;
    // Promedio de puntos
    const playersWithPoints = players.filter(p => typeof p.points === 'number' && p.points > 0);
    const avgPoints = playersWithPoints.length > 0 ? Math.round(playersWithPoints.reduce((a, p) => a + (p.points || 0), 0) / playersWithPoints.length) : 0;

    return { male, female, otherGender, diestro, zurdo, handUnset, drive, reves, ambas, posUnset, withAchievements, totalAchievements, suspended, guests, withPhoto, avgPoints };
  }, [players]);

  // ── CLUBES CON SU STAFF ──
  const clubsWithStaff = useMemo(() => {
    const admins = sedesPublicas.filter(s => s.role === 'admin');
    const staffMembers = allUsers.filter(s => ['co_admin', 'empleado', 'gestor_torneos', 'gestor_turnos'].includes(s.role));

    const roleLabels: Record<string, string> = {
      co_admin: 'Co-Admin',
      empleado: 'Empleado',
      gestor_torneos: 'G. Torneos',
      gestor_turnos: 'G. Turnos',
    };
    const roleColors: Record<string, string> = {
      co_admin: 'bg-sky-500/15 text-sky-400 border-sky-500/20',
      empleado: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
      gestor_torneos: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
      gestor_turnos: 'bg-teal-500/15 text-teal-400 border-teal-500/20',
    };

    return admins.map(admin => ({
      id: admin.uid,
      name: admin.name || 'Club',
      photo: admin.photoUrl,
      city: admin.city || admin.location || '',
      staff: staffMembers
        .filter(s => (s as any).clubId === admin.uid)
        .map(s => ({
          name: s.name || s.email,
          photo: s.photoUrl,
          role: roleLabels[s.role] || s.role,
          roleColor: roleColors[s.role] || 'bg-white/10 text-white/60 border-white/10',
        })),
    })).sort((a, b) => b.staff.length - a.staff.length);
  }, [sedesPublicas]);

  // ── DISTRIBUCIÓN POR CATEGORÍA ──
  const categoryDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    players.forEach(p => {
      const cat = p.category || 'Sin categoría';
      counts[cat] = (counts[cat] || 0) + 1;
    });

    const total = players.length || 1;
    return Object.entries(counts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count);
  }, [players]);

  // ── ACTIVIDAD POR SEDE ──
  const sedeActivity = useMemo(() => {
    const sedes = sedesPublicas.filter(s => s.role === 'admin');
    const matchesBySede: Record<string, number> = {};
    const tournamentsBySede: Record<string, number> = {};

    matches.forEach(m => {
      if (m.organizerId) {
        matchesBySede[m.organizerId] = (matchesBySede[m.organizerId] || 0) + 1;
      }
    });

    tournaments.forEach(t => {
      if (t.organizerId) {
        tournamentsBySede[t.organizerId] = (tournamentsBySede[t.organizerId] || 0) + 1;
      }
    });

    return sedes.map(sede => ({
      id: sede.uid,
      name: sede.name || 'Club',
      photo: sede.photoUrl,
      matches: matchesBySede[sede.uid] || 0,
      tournaments: tournamentsBySede[sede.uid] || 0,
      total: (matchesBySede[sede.uid] || 0) + (tournamentsBySede[sede.uid] || 0)
    })).sort((a, b) => b.total - a.total);
  }, [sedesPublicas, matches, tournaments]);

  // ── CURVA DE CRECIMIENTO (Registros últimos 12 meses) ──
  const growthData = useMemo(() => {
    const now = new Date();
    const months: { label: string; count: number; startMs: number; endMs: number }[] = [];

    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      months.push({
        label: d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', ''),
        count: 0,
        startMs: d.getTime(),
        endMs: nextMonth.getTime()
      });
    }

    players.forEach(p => {
      if (p.createdAt) {
        const monthEntry = months.find(m => p.createdAt >= m.startMs && p.createdAt < m.endMs);
        if (monthEntry) monthEntry.count++;
      }
    });

    return months.map(m => ({ label: m.label, count: m.count }));
  }, [players]);

  // ── SVG para el gráfico de crecimiento ──
  const growthSvg = useMemo(() => {
    const width = 800;
    const height = 200;
    const px = 50;
    const py = 25;
    const chartW = width - px * 2;
    const chartH = height - py * 2;
    const maxVal = Math.max(...growthData.map(d => d.count), 1);

    const points = growthData.map((d, i) => ({
      x: px + (i / Math.max(1, growthData.length - 1)) * chartW,
      y: height - py - (d.count / maxVal) * chartH,
      ...d
    }));

    let linePath = '';
    let areaPath = '';

    if (points.length > 1) {
      linePath = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
      areaPath = `M ${points[0].x},${height - py} L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},${height - py} Z`;
    }

    return { width, height, points, linePath, areaPath, maxVal };
  }, [growthData]);

  // ── COLORES PARA CATEGORÍAS ──
  const categoryColors = [
    'from-emerald-500 to-emerald-400',
    'from-cyan-500 to-cyan-400',
    'from-violet-500 to-violet-400',
    'from-amber-500 to-amber-400',
    'from-rose-500 to-rose-400',
    'from-sky-500 to-sky-400',
    'from-fuchsia-500 to-fuchsia-400',
    'from-lime-500 to-lime-400',
    'from-orange-500 to-orange-400',
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-500">

      {/* ── HEADER ── */}
      <div className="bg-gradient-to-r from-neutral-900 to-black rounded-3xl p-6 md:p-8 relative overflow-hidden backdrop-blur-md shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-80 bg-theme-primary/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-theme-primary/10 theme-primary-text shadow-[0_0_15px_rgba(var(--glow-rgb),0.1)]">
              <BarChart3 className="w-6 h-6" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight uppercase">
              Panel de Control Master
            </h1>
          </div>
          <p className="text-white/40 text-xs md:text-sm mt-2 font-medium max-w-xl">
            Vista general de la plataforma. Crecimiento, actividad de sedes, distribución de categorías y más.
          </p>
        </div>
      </div>

      {/* ── KPIs PRINCIPALES (4 TARJETAS) ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Usuarios de la App',
            value: kpis.totalAppUsers,
            sub: `${kpis.totalPlayers} son Jugadores`,
            icon: <Users className="w-5 h-5" />,
            color: 'text-emerald-400',
            bgGlow: 'bg-emerald-500/5',
            iconBg: 'bg-emerald-500/10'
          },
          {
            label: 'Sedes y Clubes',
            value: kpis.activeSedes,
            sub: 'Clubes Autorizados',
            icon: <MapPin className="w-5 h-5" />,
            color: 'text-cyan-400',
            bgGlow: 'bg-cyan-500/5',
            iconBg: 'bg-cyan-500/10'
          },
          {
            label: 'Partidos Totales',
            value: kpis.totalMatches,
            sub: `${kpis.totalTournaments} Torneos`,
            icon: <Trophy className="w-5 h-5" />,
            color: 'text-amber-400',
            bgGlow: 'bg-amber-500/5',
            iconBg: 'bg-amber-500/10'
          },
          {
            label: 'Nuevos (Mes)',
            value: kpis.newPlayersMonth,
            sub: `+${kpis.newPlayersWeek} esta semana`,
            icon: <UserPlus className="w-5 h-5" />,
            color: 'text-violet-400',
            bgGlow: 'bg-violet-500/5',
            iconBg: 'bg-violet-500/10'
          }
        ].map((kpi, i) => (
          <div key={i} className="bg-neutral-900/40 rounded-2xl p-5 relative overflow-hidden transition-all duration-300 group">
            <div className={`absolute top-0 right-0 w-28 h-28 ${kpi.bgGlow} blur-[40px] rounded-full pointer-events-none`} />
            <div className="flex items-start justify-between relative z-10">
              <div className="space-y-1">
                <span className="text-white/40 text-[9px] font-black uppercase tracking-wider block">{kpi.label}</span>
                <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight">{kpi.value.toLocaleString('es-AR')}</h3>
                <p className="text-white/30 text-[10px] font-medium">{kpi.sub}</p>
              </div>
              <div className={`p-2 rounded-xl ${kpi.iconBg} ${kpi.color}`}>{kpi.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── STATS SECUNDARIOS (BADGES COMPACTOS) ── */}
      <div className="flex flex-wrap gap-3 justify-center">
        {[
          { label: 'Validados', value: kpis.validatedPlayers, icon: <ShieldCheck size={13} />, color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' },
          { label: 'On Fire 🔥', value: kpis.onFirePlayers, icon: <Flame size={13} />, color: 'text-orange-400 bg-orange-400/10 border-orange-400/20' },
          { label: 'Torneos Activos', value: kpis.activeTournaments, icon: <Trophy size={13} />, color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
          { label: 'Pend. Validación', value: kpis.pendingValidation, icon: <Clock size={13} />, color: 'text-violet-400 bg-violet-400/10 border-violet-400/20' },
        ].map((badge, i) => (
          <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-wider ${badge.color}`}>
            {badge.icon}
            <span>{badge.label}:</span>
            <span className="text-white font-black">{badge.value}</span>
          </div>
        ))}
      </div>

      {/* ── DEMOGRAFÍA: GÉNERO / MANO / POSICIÓN ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Género */}
        <div className="bg-neutral-900/40 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-pink-500/5 blur-[40px] rounded-full pointer-events-none" />
          <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-white/40 mb-4 relative z-10">👤 Género</h4>
          <div className="space-y-3 relative z-10">
            {[
              { label: 'Masculino', value: demographics.male, color: 'from-sky-500 to-sky-400', emoji: '♂️' },
              { label: 'Femenino', value: demographics.female, color: 'from-pink-500 to-pink-400', emoji: '♀️' },
              ...(demographics.otherGender > 0 ? [{ label: 'No especificado', value: demographics.otherGender, color: 'from-gray-500 to-gray-400', emoji: '·' }] : [])
            ].map(item => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-white/60">{item.emoji} {item.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-black text-white">{item.value}</span>
                    <span className="text-[9px] text-white/30 font-bold">({players.length ? Math.round((item.value / players.length) * 100) : 0}%)</span>
                  </div>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`} style={{ width: `${players.length ? (item.value / players.length) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mano dominante */}
        <div className="bg-neutral-900/40 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 blur-[40px] rounded-full pointer-events-none" />
          <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-white/40 mb-4 relative z-10">✋ Mano Dominante</h4>
          <div className="space-y-3 relative z-10">
            {[
              { label: 'Diestros', value: demographics.diestro, color: 'from-amber-500 to-amber-400', emoji: '🤚' },
              { label: 'Zurdos', value: demographics.zurdo, color: 'from-emerald-500 to-emerald-400', emoji: '🫲' },
              ...(demographics.handUnset > 0 ? [{ label: 'Sin definir', value: demographics.handUnset, color: 'from-gray-500 to-gray-400', emoji: '·' }] : [])
            ].map(item => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-white/60">{item.emoji} {item.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-black text-white">{item.value}</span>
                    <span className="text-[9px] text-white/30 font-bold">({players.length ? Math.round((item.value / players.length) * 100) : 0}%)</span>
                  </div>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`} style={{ width: `${players.length ? (item.value / players.length) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Posición preferida */}
        <div className="bg-neutral-900/40 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-violet-500/5 blur-[40px] rounded-full pointer-events-none" />
          <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-white/40 mb-4 relative z-10">🏓 Posición Preferida</h4>
          <div className="space-y-3 relative z-10">
            {[
              { label: 'Drive', value: demographics.drive, color: 'from-violet-500 to-violet-400' },
              { label: 'Revés', value: demographics.reves, color: 'from-fuchsia-500 to-fuchsia-400' },
              { label: 'Ambas', value: demographics.ambas, color: 'from-indigo-500 to-indigo-400' },
              ...(demographics.posUnset > 0 ? [{ label: 'Sin definir', value: demographics.posUnset, color: 'from-gray-500 to-gray-400' }] : [])
            ].map(item => (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-white/60">{item.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-black text-white">{item.value}</span>
                    <span className="text-[9px] text-white/30 font-bold">({players.length ? Math.round((item.value / players.length) * 100) : 0}%)</span>
                  </div>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`} style={{ width: `${players.length ? (item.value / players.length) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── STAFF & USUARIOS + EXTRA STATS ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Clubes y su Staff */}
        <div className="bg-neutral-900/40 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 blur-[40px] rounded-full pointer-events-none" />
          <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-white/40 mb-3 relative z-10">🏢 Clubes y su Staff</h4>
          <div className="space-y-3 relative z-10 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
            {clubsWithStaff.map(club => (
              <div key={club.id} className="bg-white/[0.03] border border-white/5 rounded-xl p-3">
                {/* Admin / Propietario */}
                <div className="flex items-center gap-2.5 mb-2">
                  {club.photo ? (
                    <img src={club.photo} alt="" className="w-8 h-8 rounded-lg object-cover ring-1 ring-white/10" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center ring-1 ring-white/10">
                      <MapPin size={12} className="text-white/30" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h5 className="text-xs font-black text-white uppercase tracking-tight truncate">{club.name}</h5>
                    {club.city && <span className="text-[9px] text-white/30 font-medium">{club.city}</span>}
                  </div>
                  <span className="text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">
                    Propietario
                  </span>
                </div>
                {/* Staff del club */}
                {club.staff.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 pl-10">
                    {club.staff.map((member, j) => (
                      <div key={j} className="flex items-center gap-1.5 bg-white/[0.03] border border-white/5 rounded-lg px-2 py-1">
                        {member.photo ? (
                          <img src={member.photo} alt="" className="w-4 h-4 rounded-full object-cover" />
                        ) : (
                          <div className="w-4 h-4 rounded-full bg-white/10" />
                        )}
                        <span className="text-[9px] font-bold text-white/70 truncate max-w-[80px]">{member.name}</span>
                        <span className={`text-[7px] font-black uppercase px-1 py-0.5 rounded border ${member.roleColor}`}>{member.role}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[9px] text-white/20 pl-10 italic">Sin staff asignado</p>
                )}
              </div>
            ))}
            {clubsWithStaff.length === 0 && (
              <p className="text-center text-white/20 text-xs py-4">Sin clubes registrados</p>
            )}
          </div>
        </div>

        {/* Datos extra de perfil */}
        <div className="bg-neutral-900/40 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 blur-[40px] rounded-full pointer-events-none" />
          <h4 className="text-[10px] font-black uppercase tracking-[0.15em] text-white/40 mb-3 relative z-10">📊 Datos del Circuito</h4>
          <div className="grid grid-cols-2 gap-2.5 relative z-10">
            {[
              { label: 'Promedio Pts.', value: demographics.avgPoints.toLocaleString('es-AR'), icon: '⭐', color: 'text-amber-400' },
              { label: 'Con Foto', value: `${demographics.withPhoto}/${players.length}`, icon: '📸', color: 'text-sky-400' },
              { label: 'Con Logros', value: demographics.withAchievements.toString(), icon: '🏅', color: 'text-yellow-400' },
              { label: 'Medallas Total', value: demographics.totalAchievements.toString(), icon: '🥇', color: 'text-orange-400' },
              { label: 'Invitados', value: demographics.guests.toString(), icon: '👤', color: 'text-white/50' },
              { label: 'Suspendidos', value: demographics.suspended.toString(), icon: '🚫', color: demographics.suspended > 0 ? 'text-red-400' : 'text-white/30' },
            ].map(stat => (
              <div key={stat.label} className="bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                <span className="text-base">{stat.icon}</span>
                <div>
                  <span className="text-[9px] font-bold text-white/40 uppercase tracking-wider block">{stat.label}</span>
                  <span className={`text-sm font-black ${stat.color}`}>{stat.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── GRÁFICO DE CRECIMIENTO + DISTRIBUCIÓN POR CATEGORÍA ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

        {/* Curva de Crecimiento */}
        <div className="lg:col-span-7 bg-neutral-900/40 rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-theme-primary/5 blur-[60px] rounded-full pointer-events-none" />
          <div className="flex items-center gap-3 mb-5 relative z-10">
            <div className="p-2 rounded-xl bg-theme-primary/10 theme-primary-text">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-black uppercase tracking-wider text-white">Crecimiento de Registros</h4>
              <p className="text-[10px] text-white/30 font-medium">Nuevos jugadores por mes (últimos 12 meses)</p>
            </div>
          </div>

          <div className="relative z-10">
            <svg viewBox={`0 0 ${growthSvg.width} ${growthSvg.height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="growthGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary, #eab308)" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="var(--color-primary, #eab308)" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
                const y = growthSvg.height - 25 - pct * (growthSvg.height - 50);
                return (
                  <g key={pct}>
                    <line x1={50} y1={y} x2={growthSvg.width - 50} y2={y} stroke="white" strokeOpacity={0.04} strokeDasharray="4 4" />
                    <text x={44} y={y + 4} textAnchor="end" fill="white" fillOpacity={0.2} fontSize={10} fontWeight="bold">
                      {Math.round(growthSvg.maxVal * pct)}
                    </text>
                  </g>
                );
              })}

              {/* Area fill */}
              {growthSvg.areaPath && (
                <path d={growthSvg.areaPath} fill="url(#growthGradient)" />
              )}

              {/* Line */}
              {growthSvg.linePath && (
                <path d={growthSvg.linePath} fill="none" stroke="var(--color-primary, #eab308)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
              )}

              {/* Dots + Labels */}
              {growthSvg.points.map((p, i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r={4} fill="var(--color-primary, #eab308)" stroke="#0f172a" strokeWidth={2} />
                  {p.count > 0 && (
                    <text x={p.x} y={p.y - 12} textAnchor="middle" fill="white" fillOpacity={0.7} fontSize={10} fontWeight="bold">
                      {p.count}
                    </text>
                  )}
                  <text x={p.x} y={growthSvg.height - 5} textAnchor="middle" fill="white" fillOpacity={0.25} fontSize={9} fontWeight="bold" textTransform="uppercase">
                    {p.label}
                  </text>
                </g>
              ))}
            </svg>
          </div>
        </div>

        {/* Distribución por Categoría */}
        <div className="lg:col-span-5 bg-neutral-900/40 rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-violet-500/5 blur-[50px] rounded-full pointer-events-none" />
          <div className="flex items-center gap-3 mb-5 relative z-10">
            <div className="p-2 rounded-xl bg-violet-500/10 text-violet-400">
              <Award className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-black uppercase tracking-wider text-white">Distribución por Categoría</h4>
              <p className="text-[10px] text-white/30 font-medium">{categoryDistribution.length} categorías detectadas</p>
            </div>
          </div>

          <div className="space-y-2.5 relative z-10 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
            {categoryDistribution.map((cat, i) => (
              <div key={cat.name} className="group">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-black text-white/80 uppercase tracking-tight">{cat.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-black text-xs">{cat.count}</span>
                    <span className="text-[10px] font-black text-white/30 bg-white/5 px-1.5 py-0.5 rounded-full">{cat.percentage}%</span>
                  </div>
                </div>
                <div className="h-2.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${categoryColors[i % categoryColors.length]} rounded-full transition-all duration-1000`}
                    style={{ width: `${cat.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RANKING DE ACTIVIDAD POR SEDE ── */}
      <div className="bg-neutral-900/40 rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-500/5 blur-[60px] rounded-full pointer-events-none" />
        <div className="flex items-center gap-3 mb-5 relative z-10">
          <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-400">
            <Activity className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-sm font-black uppercase tracking-wider text-white">Actividad por Sede</h4>
            <p className="text-[10px] text-white/30 font-medium">Ranking de partidos y torneos organizados</p>
          </div>
        </div>

        {sedeActivity.length === 0 ? (
          <div className="text-center py-8 relative z-10">
            <MapPin className="mx-auto opacity-20 mb-2 text-white" size={28} />
            <p className="text-white/30 text-xs font-bold uppercase tracking-wider">Sin datos de actividad</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 relative z-10">
            {sedeActivity.map((sede, i) => {
              const maxTotal = sedeActivity[0]?.total || 1;
              const barWidth = Math.max(5, Math.round((sede.total / maxTotal) * 100));
              return (
                <div key={sede.id} className="bg-white/[0.03] border border-white/5 rounded-2xl p-4 hover:border-cyan-500/20 transition-all group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="relative">
                      {i < 3 && (
                        <div className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black z-10 ${
                          i === 0 ? 'bg-amber-400 text-black' : i === 1 ? 'bg-gray-300 text-black' : 'bg-amber-700 text-white'
                        }`}>
                          {i + 1}
                        </div>
                      )}
                      {sede.photo ? (
                        <img src={sede.photo} alt="" className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/10" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ring-1 ring-white/10">
                          <MapPin size={16} className="text-white/30" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h5 className="text-xs font-black uppercase tracking-tight text-white truncate">{sede.name}</h5>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[9px] text-cyan-400/80 font-bold flex items-center gap-1">
                          <CalendarDays size={9} /> {sede.matches} partidos
                        </span>
                        <span className="text-[9px] text-amber-400/80 font-bold flex items-center gap-1">
                          <Trophy size={9} /> {sede.tournaments} torneos
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-1000"
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
